import { Autocomplete, AutocompleteProps, AutocompleteRenderInputParams, TextField } from '@mui/material';

import { ReactNode } from 'react';
import {
    Control,
    Controller,
    FieldPath,
    FieldPathValue,
    FieldValues,
    RegisterOptions,
    UnpackNestedValue
} from 'react-hook-form';

export type UnpackArray<ArrayType, Multiple extends boolean | undefined> = Multiple extends true
    ? ArrayType extends readonly (infer ElementType)[]
        ? ElementType
        : ArrayType
    : ArrayType;

export type FormAutoCompleteProps<
    TFieldValues extends FieldValues,
    TName extends FieldPath<TFieldValues>,
    TUnpackedValue extends UnpackArray<UnpackNestedValue<FieldPathValue<TFieldValues, TName>>, Multiple>,
    Multiple extends boolean | undefined = undefined,
    DisableClearable extends boolean | undefined = undefined,
    FreeSolo extends boolean | undefined = undefined
> = {
    name: TName;
    control: Control<TFieldValues>;
    rules?: Omit<RegisterOptions<TFieldValues, TName>, 'valueAsNumber' | 'valueAsDate' | 'setValueAs'>;
    label: string;
    renderInput?(params: AutocompleteRenderInputParams): ReactNode;
} & Omit<AutocompleteProps<TUnpackedValue, Multiple, DisableClearable, FreeSolo>, 'renderInput'>;

export function FormAutocomplete<
    TFieldValues extends FieldValues,
    TName extends FieldPath<TFieldValues>,
    TUnpackedValue extends UnpackArray<UnpackNestedValue<FieldPathValue<TFieldValues, TName>>, Multiple>,
    Multiple extends boolean | undefined = undefined,
    DisableClearable extends boolean | undefined = undefined,
    FreeSolo extends boolean | undefined = undefined
>({
    name,
    control,
    rules,
    label,
    renderInput,
    ...autoCompleteProps
}: FormAutoCompleteProps<TFieldValues, TName, TUnpackedValue, Multiple, DisableClearable, FreeSolo>) {
    return (
        <Controller
            name={name}
            control={control}
            rules={rules}
            render={({ field: { value, onChange, ...rest }, fieldState: { error } }) => {
                return (
                    <Autocomplete<TUnpackedValue, Multiple, DisableClearable, FreeSolo>
                        renderInput={params => (
                            <>
                                <TextField
                                    label={label}
                                    variant="outlined"
                                    error={!!error}
                                    helperText={error?.message}
                                    value={value ?? ''}
                                    {...params}
                                />
                                {renderInput != null && renderInput(params)}
                            </>
                        )}
                        onChange={(_, data) => onChange(data)}
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        value={(value as any) ?? null}
                        {...rest}
                        {...autoCompleteProps}
                    />
                );
            }}
        />
    );
}
